import {
  selectOrganizationId,
  useGetDemosQuery,
  useGetOrganizationQuery,
} from '@carboncopyco/data';
import {
  DemoIndicatorColoredIcon,
  PlaybookIcon,
  PlusOutlineIcon,
  RecordingIndicatorColoredIcon,
  TourIndicatorColoredIcon,
} from '@carboncopyco/icons';
import {
  PermissionAction,
  PermissionKey,
  type Playbook,
  type Workspace,
} from '@carboncopyco/types';
import {
  Button,
  Dropdown,
  DropdownItem,
  Separator,
  TextBase,
} from '@carboncopyco/ui';
import { usePlaybookAnalytics } from '@carboncopyco/utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import type { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { CreateAssetMenu } from '../../CreateAssetMenu';
import { CreateDemoModal, CreateEntityModal } from '../../Demo';
import { CreatePlaybookModal } from '../../PlaybookModal';
import { TourCaptureModals } from '../../Tour';
import { useModalActions, usePermissionsContext } from '../../utils';

type ConsoleCreateNewMenuProps = {
  triggerElement?: ReactElement;
  asChild?: boolean;
  navigateToRecorder: () => void;
  navigateToTourCapture: ({
    appUrl,
    playerToken,
    fileServiceToken,
    tourId,
    originalTourId,
    insertIndex,
  }: {
    appUrl: string;
    playerToken?: string;
    fileServiceToken?: string;
    tourId?: string;
    originalTourId?: string;
    insertIndex?: number;
  }) => void;
  goToDemo: (id: string, tab?: string) => void;
  navigateToTourFromDemo: (id: number, originalTourId?: number) => void;
  handleOpenRecorder: () => void;
  goToCreateOverlayDemoPage: () => void;
  goToPlaybook?: (id: Playbook['id'], workspace: Workspace['id']) => void;
};

export const ConsoleCreateNewMenu = ({
  triggerElement,
  asChild,
  navigateToRecorder,
  navigateToTourCapture,
  goToDemo,
  navigateToTourFromDemo,
  handleOpenRecorder,
  goToCreateOverlayDemoPage,
  goToPlaybook,
}: ConsoleCreateNewMenuProps) => {
  const flags = useFlags();
  const { modalActionsByName } = useModalActions();
  const createNewDemoModal = modalActionsByName('create-new-demo-modal');
  const selectBaseForDemo = modalActionsByName('select-base-for-demo-modal');
  const createTour = modalActionsByName('create-tour-modal');
  const createPlaybook = modalActionsByName('create-playbook-modal');

  const { getPermissions } = usePermissionsContext();

  const handleClose = () => {
    createNewDemoModal.closeModal();
    selectBaseForDemo.closeModal();
  };

  const createADemoPlanPermission = getPermissions(
    PermissionKey.demo,
    PermissionAction.createADemo
  );

  const createRecordingPlanPermission = getPermissions(
    PermissionKey.record,
    PermissionAction.createRecording
  );
  const createPlaybookPermission = getPermissions(
    PermissionKey.playbook,
    PermissionAction.createPlaybook
  );
  const { trackPlaybookClickCreate } = usePlaybookAnalytics('console');

  const organizationId = useSelector(selectOrganizationId);

  const { data } = useGetDemosQuery({
    organizationId,
  });
  const demos = data?.items;
  const { data: organization } = useGetOrganizationQuery({
    id: organizationId,
  });

  return (
    <>
      {flags.playbook ? (
        <CreateAssetMenu
          triggerElement={
            triggerElement || (
              <Button
                dark
                leftIcon={<PlusOutlineIcon />}
                label="Create new"
              />
            )
          }
          onCreateDemo={() => {
            createADemoPlanPermission
              ? selectBaseForDemo.openModal()
              : goToCreateOverlayDemoPage();
          }}
          onCreateTour={() => createTour.openModal()}
          onCreateClone={handleOpenRecorder}
          onCreatePlaybook={() => {
            createPlaybook.openModal();
            trackPlaybookClickCreate();
          }}
        />
      ) : (
        <Dropdown
          asChild={asChild}
          align="center"
          arrow
          width={136}
          triggerElement={triggerElement}
        >
          <div className="p-1.5">
            <DropdownItem
              asChild
              onClick={() => {
                createADemoPlanPermission
                  ? selectBaseForDemo.openModal()
                  : goToCreateOverlayDemoPage();
              }}
            >
              <div className="flex p-2.5 gap-2.5 items-center cursor-pointer rounded-md">
                <DemoIndicatorColoredIcon />
                <TextBase
                  label="Demo"
                  color="text-ds-natural-600"
                />
              </div>
            </DropdownItem>
            <DropdownItem
              asChild
              onClick={() => createTour.openModal()}
            >
              <div className="flex p-2.5 items-center gap-2.5 rounded-md !cursor-pointer">
                <TourIndicatorColoredIcon />
                <TextBase
                  label="Tour"
                  color="text-ds-natural-600"
                />
              </div>
            </DropdownItem>
            {flags.playbook && createPlaybookPermission && (
              <DropdownItem
                onClick={() => {
                  createPlaybook.openModal();
                  trackPlaybookClickCreate();
                }}
              >
                <div className="flex p-2.5 items-center gap-2.5 rounded-md cursor-pointer">
                  <PlaybookIcon />
                  <TextBase
                    label="Playbook"
                    color="text-ds-natural-600"
                  />
                </div>
              </DropdownItem>
            )}
            {createRecordingPlanPermission && (
              <>
                <div className="my-1">
                  <Separator horizontal />
                </div>
                <DropdownItem
                  asChild
                  onClick={navigateToRecorder}
                >
                  <div className="flex p-2.5 items-center gap-2.5 rounded-md !cursor-pointer">
                    <RecordingIndicatorColoredIcon />
                    <TextBase
                      label="Clone"
                      color="text-ds-natural-600"
                    />
                  </div>
                </DropdownItem>
              </>
            )}
          </div>
        </Dropdown>
      )}
      {selectBaseForDemo.isModalOpen() && (
        <CreateEntityModal
          base="demo"
          open={selectBaseForDemo.isModalOpen()}
          onClose={selectBaseForDemo.closeModal}
          handleContinue={(selected) => {
            if (selected === 'clone') {
              selectBaseForDemo.closeModal();
              createNewDemoModal.openModal();
              return;
            }
            if (selected === 'overlay') {
              goToCreateOverlayDemoPage();
              handleClose();
            }
          }}
          isTourFromUrlDisabled={!demos?.length}
        />
      )}
      <TourCaptureModals
        showCreateTourModal={createTour.isModalOpen()}
        hide={() => createTour.closeModal()}
        navigateToWebTourCapture={navigateToTourCapture}
        navigateToTourFromDemo={navigateToTourFromDemo}
        goToDemo={goToDemo}
        handleOpenRecorder={handleOpenRecorder}
        goToCreateOverlayDemoPage={goToCreateOverlayDemoPage}
      />
      {createNewDemoModal.isModalOpen() && (
        <CreateDemoModal
          noBackground
          open={createNewDemoModal.isModalOpen()}
          onClose={handleClose}
          handleOpenRecorder={() => {
            navigateToRecorder();
            handleClose();
          }}
          goToDemo={goToDemo}
        />
      )}
      {createPlaybook.isModalOpen() && (
        <CreatePlaybookModal
          workspaceId={String(organization.defaultWorkspace)}
          onClose={() => createPlaybook.closeModal()}
          onSuccess={goToPlaybook}
        />
      )}
    </>
  );
};
